import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { useState } from "react";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { Select } from "@radix-ui/themes";
import { isEmpty } from "lodash-es";

export const AdminMemberBadgesCreatePage = () => {
  const [staffMemberId, setStaffMemberId] = useState("");
  const [staffBadgeId, setStaffBadgeId] = useState("");
  const [context, setContext] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch all staff members
  const { data: staffMembersData, isLoading: isLoadingMembers } = useQuery({
    queryKey: ["staff-members-list"],
    queryFn: () =>
      api.post("/internal/v1/staff/members/search", {
        body: {
          query_string: "",
          limit: 1000, // Get all members
        },
      }),
  });

  // Fetch all badges
  const { data: badgesData, isLoading: isLoadingBadges } = useQuery({
    queryKey: ["staff-badges-list"],
    queryFn: () =>
      api.post("/internal/v1/staff/badges/search", {
        body: {
          query_string: "",
          limit: 1000, // Get all badges
        },
      }),
  });

  const staffMembers = staffMembersData?.data?.results ?? [];
  const badges = badgesData?.data?.results ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post("/internal/v1/staff/member-badges", {
      body: {
        staff_member_id: staffMemberId,
        staff_badge_id: staffBadgeId,
        context: isEmpty(context) ? null : context,
      },
    });

    // Clear user and context fields, but keep badge selected
    setStaffMemberId("");
    setContext("");
    setShowSuccess(true);

    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  return (
    <Container>
      <h1>Award Badge to Member</h1>
      {showSuccess && <SuccessMessage>Success! Badge awarded.</SuccessMessage>}
      <Form onSubmit={handleSubmit}>
        <FormField>
          <Label>Badge</Label>
          <Select.Root
            value={staffBadgeId}
            onValueChange={setStaffBadgeId}
            disabled={isLoadingBadges}
          >
            <Select.Trigger placeholder="Select a badge" />
            <Select.Content>
              {badges.map(badge => (
                <Select.Item key={badge.id} value={badge.id}>
                  {badge.name} - {badge.slug}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </FormField>

        <FormField>
          <Label>Staff Member</Label>
          <Select.Root
            value={staffMemberId}
            onValueChange={setStaffMemberId}
            disabled={isLoadingMembers}
          >
            <Select.Trigger placeholder="Select a staff member" />
            <Select.Content>
              {staffMembers.map(member => (
                <Select.Item key={member.id} value={member.id}>
                  {member.profile_display_name} ({member.staff_email_address})
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </FormField>

        <FormField>
          <Label>Context</Label>
          <TextArea
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="Explain why this badge is being awarded..."
            rows={4}
          />
        </FormField>

        <SubmitButton
          type="submit"
          disabled={!staffMemberId || !staffBadgeId || isLoadingMembers || isLoadingBadges}
        >
          Award Badge
        </SubmitButton>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SubmitButton = styled(Button)`
  margin-top: 16px;
  align-self: flex-start;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;
